import { pickKaraokeVariantByLanguage } from './karaokeLanguageVariants';

import type { Preset } from './types';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeTextBranding } from '@src/types.ts/story';
import type { AspectRatio } from '@src/types.ts/video';

/**
 * Minimal subset of TextBrandingService needed by KaraokePresetProvider
 */
export interface TextBrandingServiceLike {
  getBrandingProps: (branding: KaraokeTextBranding, type: 'karaoke') => Preset;
  transformKaraokeTemplateIntoConfig: (template: Preset) => KaraokeConfig;
}

export interface KaraokePresetProviderDeps {
  presets: Preset[];
  branding: TextBrandingServiceLike;
  userTemplates: KaraokeTextBranding[]; // All user templates across all aspect ratios
}

/**
 * Read-only provider for discovering and applying karaoke template variants
 * across different aspect ratios.
 *
 * Responsibilities:
 * - Look up template variants by presetId and aspect ratio
 * - Transform templates into KaraokeConfig objects
 */
export interface IKaraokePresetProvider {
  /**
   * Apply a template variant to a config for the target aspect ratio.
   * Returns null if no variant exists.
   */
  applyVariant(
    current: KaraokeConfig,
    aspectRatio: AspectRatio,
  ): KaraokeConfig | null;

  /**
   * Get the template variant for a given parent template ID and aspect ratio.
   * Returns null if no variant exists.
   */
  getVariantForAspectRatio(
    parentTemplateId: string,
    aspectRatio: AspectRatio,
  ): KaraokeTextBranding | Preset | null;
}

export class KaraokePresetProvider implements IKaraokePresetProvider {
  private readonly presets: Preset[];
  private readonly branding: TextBrandingServiceLike;
  private readonly userTemplates: KaraokeTextBranding[];

  constructor(deps: KaraokePresetProviderDeps) {
    this.presets = deps.presets;
    this.branding = deps.branding;
    this.userTemplates = deps.userTemplates;
  }

  applyVariant(
    current: KaraokeConfig,
    aspectRatio: AspectRatio,
  ): KaraokeConfig | null {
    const parentId = current.parent_template_id;
    if (!parentId) return null;

    // First check presets (global templates)
    const presetVariant = this.presets.find(
      p => p.id === parentId && p.aspectRatio === aspectRatio,
    );
    if (presetVariant) {
      const unpacked =
        this.branding.transformKaraokeTemplateIntoConfig(presetVariant);
      return {
        ...current,
        ...unpacked,
        parent_template_id: parentId,
      };
    }

    // Check user templates - match by the preset FAMILY and aspect ratio.
    // `parentId` may be the family root's own id, or a variant's id once one has
    // been applied, so resolve it to the family key first — matching `parentId`
    // directly missed the sibling on a switch to a third aspect ratio and fell
    // through to hand-scaling (PLAT-11542). Same resolution
    // `KaraokeTemplateVariantService.ensureVariantsForConfigs` does.
    // PLAT-11416: among the family's records for this aspect ratio, prefer
    // the one curated for the config's caption language, then the
    // language-neutral one — otherwise an AR switch while in Japanese would
    // silently rebind to the Latin variant.
    const parentTemplate = this.userTemplates.find(t => t.id === parentId);
    // `||`, not `??`: DatoCMS returns '' for an unset string field, and an
    // empty familyId would match every never-varianted template below.
    const familyId = parentTemplate?.presetId || parentId;
    const familyCandidates = this.userTemplates.filter(
      t =>
        t.aspectRatio === aspectRatio &&
        (t.id === familyId || t.presetId === familyId),
    );
    const userTemplate = pickKaraokeVariantByLanguage(
      familyCandidates,
      current.language,
    );

    if (userTemplate) {
      // Convert DatoColor objects to rgba strings
      const formattedTemplate = this.branding.getBrandingProps(
        userTemplate,
        'karaoke',
      );
      const unpacked =
        this.branding.transformKaraokeTemplateIntoConfig(formattedTemplate);
      return {
        ...current,
        ...unpacked,
        parent_template_id:
          userTemplate.id || userTemplate.presetId || parentId,
      };
    }

    return null;
  }

  getVariantForAspectRatio(
    parentTemplateId: string,
    aspectRatio: AspectRatio,
  ): KaraokeTextBranding | Preset | null {
    // Check presets
    const presetVariant = this.presets.find(
      p => p.id === parentTemplateId && p.aspectRatio === aspectRatio,
    );
    if (presetVariant) {
      return presetVariant;
    }

    // Check user templates
    const userTemplate = this.userTemplates.find(
      t =>
        t.aspectRatio === aspectRatio &&
        (t.id === parentTemplateId || t.presetId === parentTemplateId),
    );

    return userTemplate || null;
  }
}
